import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Sparkles, Building2, Globe, Users, Briefcase, Mail, X, Loader2, 
  ChevronRight, ChevronLeft, Rocket, Check, PartyPopper, ArrowRight,
  LayoutDashboard, Package, Zap, Clock
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useSafeMode } from '../contexts/SafeModeContext';
import { API_BASE_URL } from '../config/api';
import { COMMON_TIMEZONES, getBrowserTimezone } from '../utils/dateUtils';

const BACKEND_URL = API_BASE_URL;

const INDUSTRIES = [
  'SaaS / Software',
  'E-commerce / Retail',
  'Fintech / Finance',
  'Healthcare / Biotech',
  'Education / EdTech',
  'Media / Entertainment',
  'Manufacturing',
  'Professional Services',
  'Other'
];

const ROLES = [
  'Product Manager',
  'Founder / CEO',
  'Engineering Lead',
  'Designer',
  'Marketing',
  'Operations',
  'Other'
];

const TEAM_SIZES = [
  { value: '1', label: 'Just me' },
  { value: '2-5', label: '2-5 people' },
  { value: '6-20', label: '6-20 people' },
  { value: '20+', label: '20+ people' }
];

// Progress Indicator Component (moved outside)
const ProgressIndicator = ({ currentStep, showInviteSection, totalSteps, safeMode }) => (
  <div className="flex items-center justify-center gap-2 mb-6">
    {[1, 2].map((step) => {
      if (step === 2 && !showInviteSection) return null;
      
      const isActive = currentStep === step;
      const isCompleted = currentStep > step;
      
      return (
        <React.Fragment key={step}>
          <div className={`flex items-center gap-2 ${isActive ? 'opacity-100' : 'opacity-60'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
              isCompleted 
                ? 'bg-green-500 text-white'
                : isActive 
                  ? safeMode ? 'bg-blue-500 text-white' : 'bg-red-500 text-white'
                  : 'bg-white/20 text-white/60'
            }`}>
              {isCompleted ? <Check className="w-4 h-4" /> : step}
            </div>
            <span className={`text-sm hidden sm:inline ${isActive ? 'text-white font-medium' : 'text-white/60'}`}>
              {step === 1 ? 'Company Info' : 'Invite Team'}
            </span>
          </div>
          {step < totalSteps && showInviteSection && (
            <div className={`w-8 h-0.5 ${currentStep > step ? 'bg-green-500' : 'bg-white/20'}`} />
          )}
        </React.Fragment>
      );
    })}
  </div>
);

// Welcome Modal Component (moved outside)
const WelcomeModal = ({ safeMode, userName, onGetStarted }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
    <Card className={`relative z-10 w-full max-w-lg p-8 text-center backdrop-blur-lg transition-all duration-500 ${
      safeMode 
        ? 'bg-white/95 border-gray-200'
        : 'bg-slate-900/95 border-red-500/30'
    }`}>
      {/* Celebration Icon */}
      <div className={`w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center ${
        safeMode ? 'bg-blue-100' : 'bg-red-500/20'
      }`}>
        <PartyPopper className={`w-10 h-10 ${safeMode ? 'text-blue-600' : 'text-red-400'}`} />
      </div>
      
      <h2 className={`text-2xl font-bold mb-2 ${safeMode ? 'text-gray-900' : 'text-white'}`}>
        {safeMode ? "You're All Set!" : "Arsenal Activated!"}
      </h2>
      
      <p className={`mb-6 ${safeMode ? 'text-gray-600' : 'text-gray-400'}`}>
        {safeMode 
          ? `Welcome to StackRanked.ai, ${userName || 'there'}! Your workspace is ready.`
          : `${userName || 'Commander'}, your command center is fully operational.`
        }
      </p>
      
      {/* What's Next Section */}
      <div className={`rounded-lg p-4 mb-6 text-left ${
        safeMode ? 'bg-gray-50' : 'bg-white/5'
      }`}>
        <h3 className={`text-sm font-semibold mb-3 ${safeMode ? 'text-gray-700' : 'text-gray-300'}`}>
          {safeMode ? "Here's what you can do next:" : "Your next moves:"}
        </h3>
        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <div className={`p-1.5 rounded ${safeMode ? 'bg-blue-100' : 'bg-red-500/20'}`}>
              <Package className={`w-4 h-4 ${safeMode ? 'text-blue-600' : 'text-red-400'}`} />
            </div>
            <div>
              <p className={`text-sm font-medium ${safeMode ? 'text-gray-800' : 'text-white'}`}>
                Create your first product
              </p>
              <p className={`text-xs ${safeMode ? 'text-gray-500' : 'text-gray-500'}`}>
                Define products and let AI break them down
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className={`p-1.5 rounded ${safeMode ? 'bg-blue-100' : 'bg-red-500/20'}`}>
              <Zap className={`w-4 h-4 ${safeMode ? 'text-blue-600' : 'text-red-400'}`} />
            </div>
            <div>
              <p className={`text-sm font-medium ${safeMode ? 'text-gray-800' : 'text-white'}`}>
                Use AI to generate requirements
              </p>
              <p className={`text-xs ${safeMode ? 'text-gray-500' : 'text-gray-500'}`}>
                Automatically create epics, features & user stories
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className={`p-1.5 rounded ${safeMode ? 'bg-blue-100' : 'bg-red-500/20'}`}>
              <LayoutDashboard className={`w-4 h-4 ${safeMode ? 'text-blue-600' : 'text-red-400'}`} />
            </div>
            <div>
              <p className={`text-sm font-medium ${safeMode ? 'text-gray-800' : 'text-white'}`}>
                Track progress on your dashboard
              </p>
              <p className={`text-xs ${safeMode ? 'text-gray-500' : 'text-gray-500'}`}>
                Monitor goals, metrics, and team activity
              </p>
            </div>
          </div>
        </div>
      </div>
      
      <Button
        onClick={onGetStarted}
        className={`w-full h-12 text-white font-semibold text-lg gap-2 transition-all duration-500 ${
          safeMode 
            ? 'bg-blue-600 hover:bg-blue-700'
            : 'bg-gradient-to-r from-red-600 to-orange-500 hover:from-red-500 hover:to-orange-400 shadow-lg shadow-red-500/30'
        }`}
      >
        {safeMode ? "Go to Dashboard" : "Enter Command Center"}
        <ArrowRight className="w-5 h-5" />
      </Button>
    </Card>
  </div>
);

const OnboardingPage = ({ createOrgMode = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, organization, refreshAuth } = useAuth();
  const { safeMode, setSafeMode, saveUserPreference } = useSafeMode();
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  
  // Get context for removed users (from session storage)
  const [removedFromOrg, setRemovedFromOrg] = useState(null);
  const [accessMessage, setAccessMessage] = useState(null);
  
  // Get user data from either auth context or navigation state
  const authUser = user || location.state?.user;

  // Check for removed user context on mount
  useEffect(() => {
    const removed = sessionStorage.getItem('removed_from_org');
    const message = sessionStorage.getItem('access_message');
    if (removed) {
      setRemovedFromOrg(removed);
      setAccessMessage(message);
      sessionStorage.removeItem('removed_from_org');
      sessionStorage.removeItem('access_message');
    }
  }, []);

  // Check if user signed up with Safe Mode OFF and apply that preference
  useEffect(() => {
    const signupSafeMode = sessionStorage.getItem('signup_safe_mode');
    if (signupSafeMode !== null) {
      const wasInDarkMode = JSON.parse(signupSafeMode) === false;
      if (wasInDarkMode) {
        setSafeMode(false);
      }
      sessionStorage.removeItem('signup_safe_mode');
    }
  }, [setSafeMode]);
  
  // Form state - Step 1
  const [companyName, setCompanyName] = useState('');
  const [companyWebsite, setCompanyWebsite] = useState('');
  const [industry, setIndustry] = useState('');
  const [userRole, setUserRole] = useState('');
  const [teamSize, setTeamSize] = useState('');
  const [userTimezone, setUserTimezone] = useState(() => getBrowserTimezone());
  
  // Form state - Step 2
  const [inviteEmails, setInviteEmails] = useState([]);
  const [currentEmail, setCurrentEmail] = useState('');
  
  const addInviteEmail = () => {
    const email = currentEmail.trim().toLowerCase();
    if (!email) return;
    
    if (!email.includes('@') || !email.includes('.')) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    if (inviteEmails.includes(email)) {
      toast.error('This email is already added');
      return;
    }
    
    setInviteEmails([...inviteEmails, email]);
    setCurrentEmail('');
  };
  
  const removeInviteEmail = (email) => {
    setInviteEmails(inviteEmails.filter(e => e !== email));
  };

  const validateStep1 = () => {
    if (!companyName.trim()) {
      toast.error('Please enter your company name');
      return false;
    }
    if (!industry) {
      toast.error('Please select your industry');
      return false;
    }
    if (!userRole) {
      toast.error('Please select your role');
      return false;
    }
    if (!teamSize) {
      toast.error('Please select your team size');
      return false;
    }
    return true;
  };

  const handleNextStep = () => {
    if (validateStep1()) {
      // If solo user, skip team invite step
      if (teamSize === '1') {
        handleSubmit();
      } else {
        setCurrentStep(2);
      }
    }
  };

  const handlePreviousStep = () => {
    setCurrentStep(1);
  };
  
  const handleSubmit = async (skipInvites = false) => {
    if (currentStep === 1 && !validateStep1()) return;
    
    setLoading(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/onboarding/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          company_name: companyName,
          company_website: companyWebsite || null,
          industry,
          user_role: userRole,
          team_size: teamSize,
          invite_emails: skipInvites ? [] : inviteEmails,
          user_timezone: userTimezone
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to complete onboarding');
      }
      
      const data = await response.json();
      
      // Save user's Safe Mode preference permanently
      saveUserPreference();
      
      // Refresh auth to get updated organization data from server
      // This is critical to prevent the "double login" bug
      await refreshAuth();
      
      // Show welcome modal instead of navigating immediately
      setShowWelcomeModal(true);
      
      // Show success toast for invites
      if (data.invites_sent > 0) {
        toast.success(`${data.invites_sent} invite(s) sent to your team!`);
      }
      
    } catch (error) {
      console.error('Onboarding error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGetStarted = () => {
    // Navigate to products page with replace to prevent back navigation to onboarding
    navigate('/products', { replace: true });
  };
  
  const showInviteSection = teamSize && teamSize !== '1';
  const totalSteps = showInviteSection ? 2 : 1;
  const userName = authUser?.name?.split(' ')[0];
  
  return (
    <div className={`min-h-screen flex items-center justify-center p-4 transition-all duration-500 ${
      safeMode 
        ? 'bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900'
        : 'bg-gradient-to-br from-black via-slate-950 to-black'
    }`}>
      <div className="w-full max-w-xl">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="flex items-center justify-center gap-2 mb-4">
            {safeMode ? (
              <Sparkles className="w-8 h-8 text-blue-400" />
            ) : (
              <Rocket className="w-8 h-8 text-red-400" />
            )}
            <span className="text-2xl font-bold text-white">StackRanked.ai</span>
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">
            {createOrgMode
              ? (safeMode ? "Create a New Organization" : "Launch New Command Center")
              : currentStep === 1 
                ? (safeMode ? "Let's set up your workspace" : "Configure Your Command Center")
                : (safeMode ? "Invite your team" : "Assemble Your Squad")
            }
          </h1>
          <p className="text-gray-400">
            {createOrgMode
              ? (safeMode 
                  ? "Start fresh with a new workspace"
                  : "New mission, new base of operations")
              : currentStep === 1
                ? (safeMode 
                    ? "Tell us about your company to personalize your experience"
                    : "Quick setup. Then we ship.")
                : (safeMode
                    ? "Collaboration makes products better"
                    : "Great products need great teams")
            }
          </p>
        </div>

        {/* Removed from org banner */}
        {removedFromOrg && (
          <div className={`mb-6 p-4 rounded-lg border ${
            safeMode 
              ? 'bg-amber-500/10 border-amber-500/30'
              : 'bg-amber-900/20 border-amber-500/30'
          }`}>
            <div className="flex items-start gap-3">
              <div className="p-1.5 rounded bg-amber-500/20">
                <Building2 className="w-4 h-4 text-amber-400" />
              </div>
              <div>
                <p className="text-amber-200 font-medium text-sm">
                  You no longer have access to "{removedFromOrg}"
                </p>
                <p className="text-amber-300/70 text-xs mt-1">
                  {accessMessage || "Contact their administrator if you believe this is a mistake, or create a new organization below."}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Progress Indicator */}
        <ProgressIndicator 
          currentStep={currentStep}
          showInviteSection={showInviteSection}
          totalSteps={totalSteps}
          safeMode={safeMode}
        />
        
        {/* Form Card */}
        <Card className={`p-8 backdrop-blur-lg transition-all duration-500 ${
          safeMode 
            ? 'bg-white/10 border-white/20'
            : 'bg-red-900/10 border-red-500/30'
        }`}>
          {/* Step 1: Company Info */}
          {currentStep === 1 && (
            <div className="space-y-5">
              {/* Company Name */}
              <div>
                <Label className="text-white flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  Company Name <span className="text-red-400">*</span>
                </Label>
                <Input
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="Acme Inc."
                  className="mt-2 bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                />
              </div>
              
              {/* Company Website */}
              <div>
                <Label className="text-white flex items-center gap-2">
                  <Globe className="w-4 h-4" />
                  Company Website <span className="text-gray-400 text-sm">(optional)</span>
                </Label>
                <Input
                  value={companyWebsite}
                  onChange={(e) => setCompanyWebsite(e.target.value)}
                  placeholder="https://acme.com"
                  className="mt-2 bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                />
              </div>
              
              {/* Industry */}
              <div>
                <Label className="text-white flex items-center gap-2">
                  <Briefcase className="w-4 h-4" />
                  Industry <span className="text-red-400">*</span>
                </Label>
                <select
                  value={industry}
                  onChange={(e) => setIndustry(e.target.value)}
                  className="mt-2 w-full h-10 px-3 rounded-md bg-white/10 border border-white/20 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="" className="bg-gray-800">Select industry...</option>
                  {INDUSTRIES.map(ind => (
                    <option key={ind} value={ind} className="bg-gray-800">{ind}</option>
                  ))}
                </select>
              </div>
              
              {/* Your Role */}
              <div>
                <Label className="text-white flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Your Role <span className="text-red-400">*</span>
                </Label>
                <select
                  value={userRole}
                  onChange={(e) => setUserRole(e.target.value)}
                  className="mt-2 w-full h-10 px-3 rounded-md bg-white/10 border border-white/20 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="" className="bg-gray-800">Select your role...</option>
                  {ROLES.map(role => (
                    <option key={role} value={role} className="bg-gray-800">{role}</option>
                  ))}
                </select>
              </div>
              
              {/* Team Size */}
              <div>
                <Label className="text-white flex items-center gap-2 mb-3">
                  <Users className="w-4 h-4" />
                  Team Size <span className="text-red-400">*</span>
                </Label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {TEAM_SIZES.map(size => (
                    <button
                      key={size.value}
                      onClick={() => setTeamSize(size.value)}
                      className={`p-3 rounded-lg border-2 transition-all text-sm font-medium ${
                        teamSize === size.value
                          ? safeMode 
                            ? 'border-blue-500 bg-blue-500/20 text-white'
                            : 'border-red-500 bg-red-500/20 text-white'
                          : 'border-white/20 text-gray-300 hover:border-white/40'
                      }`}
                    >
                      {size.label}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Timezone */}
              <div>
                <Label className="text-white flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Your Timezone
                </Label>
                <select
                  value={userTimezone}
                  onChange={(e) => setUserTimezone(e.target.value)}
                  className="mt-2 w-full h-10 px-3 rounded-md bg-white/10 border border-white/20 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {COMMON_TIMEZONES.map(tz => (
                    <option key={tz.id} value={tz.id} className="bg-gray-800">{tz.label}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-400 mt-1">
                  Used for displaying dates and times. Auto-detected from your browser.
                </p>
              </div>
              
              {/* Next/Complete Button */}
              <Button
                onClick={handleNextStep}
                disabled={loading}
                className={`w-full h-12 text-white font-semibold text-lg gap-2 transition-all duration-500 ${
                  safeMode 
                    ? 'bg-blue-600 hover:bg-blue-700'
                    : 'bg-gradient-to-r from-red-600 to-orange-500 hover:from-red-500 hover:to-orange-400 shadow-lg shadow-red-500/30'
                }`}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {safeMode ? 'Setting up...' : 'Initializing...'}
                  </>
                ) : teamSize === '1' ? (
                  <>
                    {safeMode ? 'Complete Setup' : 'Activate Arsenal'}
                    <ChevronRight className="w-5 h-5" />
                  </>
                ) : (
                  <>
                    Continue
                    <ChevronRight className="w-5 h-5" />
                  </>
                )}
              </Button>
            </div>
          )}

          {/* Step 2: Team Invites */}
          {currentStep === 2 && (
            <div className="space-y-5">
              <div>
                <Label className="text-white flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  Invite teammates <span className="text-gray-400 text-sm">(optional)</span>
                </Label>
                <p className="text-xs text-gray-400 mt-1 mb-3">
                  {safeMode 
                    ? "We'll send them an invite to join your workspace"
                    : "Bring your A-team into the fold"
                  }
                </p>
                
                {/* Email input */}
                <div className="flex gap-2">
                  <Input
                    type="email"
                    value={currentEmail}
                    onChange={(e) => setCurrentEmail(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addInviteEmail())}
                    placeholder="teammate@company.com"
                    className="flex-1 bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                  />
                  <Button
                    onClick={addInviteEmail}
                    variant="outline"
                    className="border-white/20 text-white hover:bg-white/10"
                  >
                    Add
                  </Button>
                </div>
                
                {/* Email list */}
                {inviteEmails.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {inviteEmails.map(email => (
                      <span
                        key={email}
                        className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm ${
                          safeMode 
                            ? 'bg-blue-500/20 text-blue-200'
                            : 'bg-red-500/20 text-red-200'
                        }`}
                      >
                        {email}
                        <button
                          onClick={() => removeInviteEmail(email)}
                          className="hover:text-white"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                
                {inviteEmails.length === 0 && (
                  <div className={`mt-4 p-4 rounded-lg border border-dashed ${
                    safeMode ? 'border-white/20' : 'border-red-500/30'
                  }`}>
                    <p className="text-center text-gray-400 text-sm">
                      {safeMode 
                        ? "No invites added yet. You can always invite team members later from Settings."
                        : "Flying solo for now? No problem. Recruit allies later."
                      }
                    </p>
                  </div>
                )}
              </div>
              
              {/* Action Buttons */}
              <div className="flex gap-3 pt-2">
                <Button
                  onClick={handlePreviousStep}
                  variant="outline"
                  className="flex-1 h-12 border-white/20 text-white hover:bg-white/10"
                >
                  <ChevronLeft className="w-5 h-5 mr-1" />
                  Back
                </Button>
                
                <Button
                  onClick={() => handleSubmit(false)}
                  disabled={loading}
                  className={`flex-1 h-12 text-white font-semibold gap-2 transition-all duration-500 ${
                    safeMode 
                      ? 'bg-blue-600 hover:bg-blue-700'
                      : 'bg-gradient-to-r from-red-600 to-orange-500 hover:from-red-500 hover:to-orange-400 shadow-lg shadow-red-500/30'
                  }`}
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      {safeMode ? 'Setting up...' : 'Activating...'}
                    </>
                  ) : (
                    <>
                      {inviteEmails.length > 0 
                        ? (safeMode ? 'Send Invites & Continue' : 'Deploy Squad')
                        : (safeMode ? 'Complete Setup' : 'Go Solo')
                      }
                      <ChevronRight className="w-5 h-5" />
                    </>
                  )}
                </Button>
              </div>
              
              {/* Skip link */}
              {inviteEmails.length === 0 && (
                <button
                  onClick={() => handleSubmit(true)}
                  disabled={loading}
                  className="w-full text-center text-sm text-gray-400 hover:text-white transition-colors"
                >
                  Skip for now →
                </button>
              )}
            </div>
          )}
        </Card>
        
        {/* Footer */}
        <p className="text-center text-gray-400 text-sm mt-6">
          By continuing, you agree to our Terms of Service and Privacy Policy
        </p>
      </div>

      {/* Welcome Modal */}
      {showWelcomeModal && (
        <WelcomeModal 
          safeMode={safeMode}
          userName={userName}
          onGetStarted={handleGetStarted}
        />
      )}
    </div>
  );
};

export default OnboardingPage;
